import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { VerifyMfaRequestSchema, type VerifyMfaRequest } from '@ctms/contracts';
import { AlertCircle, ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react';

import { authApi } from '../auth.api.js';
import { getApiErrorMessage } from '../../../api/client.js';

export interface MfaChallengeProps {
  mfaTicket: string;
  expiresAt: string;
  onSuccess: (token: string) => void;
  onCancel: () => void;
}

export function MfaChallenge({
  mfaTicket,
  onSuccess,
  onCancel,
}: MfaChallengeProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VerifyMfaRequest>({
    resolver: zodResolver(VerifyMfaRequestSchema),
    defaultValues: {
      mfaTicket,
      code: '',
      rememberDevice: false,
    },
  });

  const onSubmit = async (data: VerifyMfaRequest): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      const response = await authApi.verifyMfa(data);
      onSuccess(response.accessToken);
    } catch (err: unknown) {
      const msg = await getApiErrorMessage(
        err,
        'Niepoprawny lub wygasły kod 2FA. Spróbuj ponownie.',
      );
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5"
      noValidate
      aria-label="Formularz weryfikacji dwuskładnikowej 2FA"
    >
      <input type="hidden" {...register('mfaTicket')} />

      <div className="flex items-center gap-3 rounded-lg border border-cyan-800/40 bg-cyan-950/20 p-3 text-cyan-200">
        <ShieldCheck className="h-6 w-6 shrink-0 text-cyan-400" />
        <div className="text-xs">
          <p className="font-medium text-cyan-100">Weryfikacja dwuskładnikowa (2FA)</p>
          <p className="text-cyan-300/80">
            Wpisz 6-cyfrowy kod z aplikacji uwierzytelniającej na Twoim telefonie.
          </p>
        </div>
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="flex items-center gap-2.5 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
        >
          <AlertCircle className="h-4 w-4 shrink-0 text-rose-400" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div>
        <label htmlFor="mfa-code" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
          6-cyfrowy kod TOTP
        </label>
        <div className="relative mt-1.5">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
            <KeyRound className="h-4 w-4" />
          </div>
          <input
            id="mfa-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={6}
            disabled={isSubmitting}
            aria-invalid={Boolean(errors.code)}
            aria-describedby={errors.code ? 'mfa-code-error' : undefined}
            placeholder="123456"
            className="w-full tracking-widest text-center font-mono text-lg rounded-md border border-slate-700 bg-slate-900 py-2.5 pr-3 pl-9 text-slate-100 placeholder-slate-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none disabled:opacity-50"
            {...register('code')}
          />
        </div>
        {errors.code && (
          <p id="mfa-code-error" className="mt-1 text-xs text-rose-400">
            {errors.code.message}
          </p>
        )}
      </div>

      {/* Variant C (trusted workstation): opt-in device trust, granted by the API
          ONLY after a genuine TOTP code — D1/D6. The httpOnly cookie itself is
          managed by the browser; this flag is the only client-side touchpoint. */}
      <div>
        <label
          htmlFor="mfa-remember-device"
          className="flex cursor-pointer items-start gap-2.5 rounded-md border border-slate-800 bg-slate-900/60 p-3 transition-colors hover:border-slate-700"
        >
          <input
            id="mfa-remember-device"
            type="checkbox"
            disabled={isSubmitting}
            className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-cyan-500 disabled:opacity-50"
            {...register('rememberDevice')}
          />
          <span className="text-xs">
            <span className="block font-medium text-slate-200">Zapamiętaj to stanowisko</span>
            <span className="mt-0.5 block text-slate-400">
              Zaufanie na 30 dni — kolejne logowania z tego komputera bez kodu 2FA. Zaznacz
              wyłącznie na swoim, zabezpieczonym stanowisku pracy.
            </span>
          </span>
        </label>
      </div>

      <div className="space-y-2 pt-1">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-cyan-600 py-2.5 px-4 text-sm font-medium text-white transition-colors hover:bg-cyan-500 focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-950 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ShieldCheck className="h-4 w-4" />
          {isSubmitting ? 'Weryfikacja…' : 'Potwierdź kod 2FA'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-800 bg-slate-900 py-2 px-4 text-xs font-medium text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200 focus:outline-none disabled:opacity-50"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Wróć do logowania hasłem
        </button>
      </div>
    </form>
  );
}
